/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import root from 'window-or-global';
import { DEFAULT_PUBLIC_GATEWAY_URL } from './index';

/*
 It returns current environment config.
 On the backend it is taken from environments variables.
 On the frontend it is expected to be assigned to window.config, by a script that fetches it from backend
 On the frontend it is expected to be assigned to window.config, by a jest config
*/

const useConfigFromGlobal = typeof window !== 'undefined' || process.env.NODE_ENV === 'test';

type PublicEnvironmentConfig = {
  DEFAULT_TIMEOUT: number;
  API_TIMEOUT_XS: number;
  API_TIMEOUT_S: number;
  API_TIMEOUT_M: number;
  API_TIMEOUT_XL: number;
  IDLE_SESSION_TIMEOUT: number;
  LOG_LEVEL: string;
  LONG_TIMEOUT: number;
  PART_CARD_IMG_URL: string;
  /**
   * The host for PDP images. Use only for `<link rel="preconnect" href="...">`
   * The images should already have the host from the Backend response
   */
  PRODUCT_IMAGE_HOST: string;
  NODE_ENV: string;
  PAYPAGE_TIMEOUT: number;
  EXTERNAL_GCP_GATEWAY_URL: string;
  REDUX_LOGGING_SAMPLE_RATE: number;
  SHOW_XM_PREVIEW_DATE: boolean;
  CMS_PREVIEW_ENABLED: boolean;
  SCANDIT_LICENSE_KEY: string;
  THREE_SIXTY_IMAGE_BASE_URL: string;
  CMS_ENV: string;
  CMS_BRANCH: string;
  CMS_API_KEY: string;
  TARGET_ENV: string;
  METRIX_URL: string;
  WORLD_PAY_PUBLIC_KEY: string;
  LOQATE_KEY_US_PROFILE: string;
  LOQATE_KEY_MX_PROFILE: string;
  LOQATE_KEY_US_CHECKOUT: string;
  LOQATE_KEY_MX_CHECKOUT: string;
  LOQATE_KEY: string;
  LOQATE_KEY_MX: string;
  BV_API_KEY: string;
  BV_BASE_URL: string;
  BV_MEDIA_BASE_URL: string;
  BV_AUTH_CALLBACK_URL: string;
  BV_MOBILE_TAG: string;
  BV_DESKTOP_TAG: string;
  RECAPTCHA_SITE_KEY: string;
  MERCHANT_PAY_PAGE_ID: string;
  GOOGLE_PAYMENT_GATEWAY: string;
  GOOGLE_PAY_MERCH_ID: string;
  GOOGLE_MAPS_API_KEY: string;
  GOOGLE_MAPS_MAP_ID: string;
  CITRUS_PROXY_DISABLED: boolean;
  PAYPAL_CLIENT_ID: string;
  BUILD_BRANCH: string;
  RETRY_DEFAULT: string;
  JSCRAMBLER_URL: string;
  DISABLE_GCP_API_CREDENTIALS: boolean;
};

function getEnvironmentConfig(): PublicEnvironmentConfig {
  return useConfigFromGlobal
    ? ((root.config || global.config) as PublicEnvironmentConfig)
    : {
        CITRUS_PROXY_DISABLED: process.env.CITRUS_PROXY_DISABLED === 'true',
        PART_CARD_IMG_URL: process.env.PART_CARD_IMG_URL || 'https://contentassets.autozone.com',
        PRODUCT_IMAGE_HOST: process.env.PRODUCT_IMAGE_HOST || 'https://contentinfo.autozone.com',
        DEFAULT_TIMEOUT: parseInt(process.env.DEFAULT_TIMEOUT || '') || 10000,
        LONG_TIMEOUT: parseInt(process.env.LONG_TIMEOUT || '') || 20000,
        API_TIMEOUT_XS: parseInt(process.env.API_TIMEOUT_XS || '') || 6000,
        API_TIMEOUT_S: parseInt(process.env.API_TIMEOUT_S || '') || 12000,
        API_TIMEOUT_M: parseInt(process.env.API_TIMEOUT_M || '') || 22000,
        API_TIMEOUT_XL: parseInt(process.env.API_TIMEOUT_XL || '') || 65000,
        LOG_LEVEL: process.env.LOG_LEVEL || 'error',
        NODE_ENV: process.env.NODE_ENV || '',
        EXTERNAL_GCP_GATEWAY_URL:
          process.env.EXTERNAL_GCP_GATEWAY_URL || DEFAULT_PUBLIC_GATEWAY_URL,
        IDLE_SESSION_TIMEOUT: parseInt(process.env.IDLE_SESSION_TIMEOUT || '') || 1000 * 60 * 30,
        PAYPAGE_TIMEOUT: parseInt(process.env.PAYPAGE_TIMEOUT || '') || 1000 * 60,
        REDUX_LOGGING_SAMPLE_RATE: parseFloat(process.env.REDUX_LOGGING_SAMPLE_RATE || '') || 0.1,
        SHOW_XM_PREVIEW_DATE: process.env.SHOW_XM_PREVIEW_DATE === 'true',
        CMS_PREVIEW_ENABLED: !!process.env.CONTENTSTACK_LIVE_PREVIEW_TOKEN,
        CMS_ENV: process.env.CONTENTSTACK_ENVIRONMENT || '',
        CMS_BRANCH: process.env.CONTENTSTACK_BRANCH || 'main',
        CMS_API_KEY: process.env.CONTENTSTACK_API_KEY || '',
        TARGET_ENV: process.env.TARGET_ENV || 'lower-env',
        METRIX_URL: process.env.METRIX_URL || '',
        SCANDIT_LICENSE_KEY: process.env.SCANDIT_LICENSE_KEY || '',
        THREE_SIXTY_IMAGE_BASE_URL: process.env.THREE_SIXTY_IMAGE_BASE_URL || '',
        WORLD_PAY_PUBLIC_KEY: process.env.WORLD_PAY_PUBLIC_KEY || '',
        LOQATE_KEY_US_PROFILE: process.env.LOQATE_KEY_US_PROFILE || '',
        LOQATE_KEY_MX_PROFILE: process.env.LOQATE_KEY_MX_PROFILE || '',
        LOQATE_KEY_US_CHECKOUT: process.env.LOQATE_KEY_US_CHECKOUT || '',
        LOQATE_KEY_MX_CHECKOUT: process.env.LOQATE_KEY_MX_CHECKOUT || '',
        LOQATE_KEY: process.env.LOQATE_KEY || '',
        LOQATE_KEY_MX: process.env.LOQATE_KEY_MX || '',
        BV_API_KEY: process.env.BV_API_KEY || '',
        BV_BASE_URL: process.env.BV_BASE_URL || '',
        BV_MEDIA_BASE_URL: process.env.BV_MEDIA_BASE_URL || '',
        BV_AUTH_CALLBACK_URL: process.env.BV_AUTH_CALLBACK_URL || '',
        BV_MOBILE_TAG: process.env.BV_MOBILE_TAG || '',
        BV_DESKTOP_TAG: process.env.BV_DESKTOP_TAG || '',
        RECAPTCHA_SITE_KEY:
          process.env.RECAPTCHA_SITE_KEY || '6LdxfT0pAAAAAMtgYXP1h8mAAO1ms7zEqiFtkpMI',
        MERCHANT_PAY_PAGE_ID: process.env.MERCHANT_PAY_PAGE_ID || 'example',
        GOOGLE_PAYMENT_GATEWAY: process.env.GOOGLE_PAYMENT_GATEWAY || 'exampleGatewayMerchantId',
        GOOGLE_PAY_MERCH_ID: process.env.GOOGLE_PAY_MERCH_ID || '',
        GOOGLE_MAPS_API_KEY: process.env.GOOGLE_MAPS_API_KEY || '',
        GOOGLE_MAPS_MAP_ID: process.env.GOOGLE_MAPS_MAP_ID || '',
        PAYPAL_CLIENT_ID:
          process.env.PAYPAL_CLIENT_ID ||
          'AbkWaSomuPmSW71ZIqWaVJ5DpxzunedVUqYlSC4QvTaD1hNnQDqVohF6WJWBpq_DRHImfy7uwol6Juz_',
        BUILD_BRANCH: 'v-7.12.0',
        RETRY_DEFAULT: process.env.RETRY_DEFAULT || '0:0:false',
        JSCRAMBLER_URL: process.env.JSCRAMBLER_URL || '',
        DISABLE_GCP_API_CREDENTIALS: process.env.DISABLE_GCP_API_CREDENTIALS === 'true',
      };
}

const environmentConfig = getEnvironmentConfig();

export default environmentConfig;

export { getEnvironmentConfig };
